<template lang="pug">
  .dropdown(
    ref="dropdown"
    :class="{'active': isActive || isHovered}"
  )
    .dropdown__overlay(v-if="!hideOverlay")
    .dropdown__block(ref="block")
      .dropdown__container
        .dropdown__menu
          slot(name="items")
</template>

<script>
import { createPopper } from '@popperjs/core/lib/popper-lite'
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow'
import flip from '@popperjs/core/lib/modifiers/flip'

export default {
  name: 'Dropdown',
  props: {
    triggerId: String,
    trigger: String,
    listeners: {
      type: String,
      default: 'click'
    },
    placement: {
      type: String,
      default: 'bottom-start'
    },
    hideOverlay: Boolean,
    noListeners: Boolean,
    usePopper: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isActive: false,
      isHovered: false,
      hoverTimeout: null,
      unhoverTimeout: null
    }
  },
  computed: {
    isShown () {
      return this.isActive || this.isHovered
    }
  },
  methods: {
    open () {
      this.isActive = true
      this.resetHover()
    },
    close () {
      this.isActive = false
      this.resetHover()
    },
    toggle () {
      this.isActive = !this.isActive
      this.resetHover()
    },
    resetHover () {
      this.isHovered = false
      clearTimeout(this.hoverTimeout)
      clearTimeout(this.unhoverTimeout)
    },
    hover () {
      if (!this.isHovered) {
        this.hoverTimeout = setTimeout(() => {
          this.isHovered = true
        }, 300)
      }
      clearTimeout(this.unhoverTimeout)
    },
    unhover () {
      if (this.isHovered) {
        this.unhoverTimeout = setTimeout(() => {
          this.isHovered = false
        }, 600)
      }
      clearTimeout(this.hoverTimeout)
    },
    setPosition () {
      this.popperInstance = createPopper(this.triggerEl, this.dropdownBlock, {
        placement: this.placement,
        modifiers: [preventOverflow, flip]
      })
    },
    checkClick (e) {
      if (!this.triggerEl.contains(e.target) && this.triggerEl !== document.activeElement) {
        this.close()
      }
    },
    defineListeners () {
      const vm = this
      // Click
      if (this.listeners.indexOf('click') !== -1) {
        this.triggerEl.addEventListener('click', vm.toggle)
        this.dropdown.addEventListener('click', vm.close)
      }
      // Hover
      if (this.listeners.indexOf('hover') !== -1 && !this.$root.isTouch) {
        this.triggerEl.addEventListener('mouseenter', this.hover)
        this.dropdown.addEventListener('mouseenter', this.hover)
        this.triggerEl.addEventListener('mouseleave', this.unhover)
        this.dropdown.addEventListener('mouseleave', this.unhover)
      }
      // Focus
      if (this.listeners.indexOf('focus') !== -1) {
        this.triggerEl.addEventListener('focus', this.open)
      }
    },
    getTriggerEl (parent) {
      return parent && parent.$refs[this.trigger]
    }
  },
  watch: {
    isShown (val) {
      const vm = this
      if (!this.noListeners) {
        document.removeEventListener('click', vm.checkClick)
      }
      if (val) {
        if (this.usePopper) {
          this.setPosition()
        }
        if (!this.noListeners) {
          document.addEventListener('click', vm.checkClick)
        }
        vm.triggerEl.classList.add('has-dropdown')
        return
      }
      vm.triggerEl.classList.remove('has-dropdown')
      if (!val && this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }
    }
  },
  mounted () {
    if (this.triggerId) {
      this.triggerEl = document.getElementById(this.triggerId)
    } else {
      this.triggerEl = this.getTriggerEl(this.$parent) || this.getTriggerEl(this.$parent.$parent)[0]
    }
    this.dropdown = this.$refs.dropdown
    this.dropdownBlock = this.$refs.block
    if (!this.noListeners) {
      this.defineListeners()
    }
  },
  beforeDestroy () {
    this.triggerEl.removeEventListener('click', this.toggle)
    this.dropdown.removeEventListener('click', this.close)
    this.triggerEl.removeEventListener('mouseenter', this.hover)
    this.dropdown.removeEventListener('mouseenter', this.hover)
    this.triggerEl.removeEventListener('mouseleave', this.unhover)
    this.dropdown.removeEventListener('mouseleave', this.unhover)
  }
}
</script>
